import React, { ReactElement, useEffect, useState } from "react";

import AppBar from '@mui/material/AppBar';
import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import "./XSifterAppHeader.css"
import UserAuthService from "../../services/UserAuthService";
import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import RunningTasksDisplay from "./RunningTasksDisplay";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { IXSifterUser } from "../../types/XSifterAppUser";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { disabled } from "@base-ui/react/internals/reason-parts";
import NotificationsService from "../../services/NotificationsService";
import { useInterval } from "./UseInterval";
import { formatNumberString } from "../../types/FormattingHelper";
import { setDisplayMessage, setDisplayMessageType } from "../../store/XSifterAppReducer";

const XSifterLogo = require('./xsifter_logo.png');
const XSifterXOnly = require('./xsifter_logo_only_32.png');
const NewQueryImage = require('./new_query_48.png');

interface IXSifterAppHeaderProps {
    currentUser: IXSifterUser | undefined;
    numAccountsNeedingApproval: number;
}

const XSifterAppHeader: React.FC<IXSifterAppHeaderProps> = (props: IXSifterAppHeaderProps) => {
    const [anchorElProfile, setAnchorElProfile] = useState<null | HTMLElement>(null);
    const userAuthService = UserAuthService.getInstance();
    const [notificationCount, setNotificationCount] = useState<number>(0);
    const [adminBadgeCount, setAdminBadgeCount]  = useState<number>(0);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const appUser = useAppSelector((state) => state.app.currentUser);
    const hasQueryTitle = useAppSelector((state) => state.app.hasQueryTitle);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();


    useEffect(() => {
        userAuthService.setSessionExpiredHandler(() => navigate('/'));
        return () => userAuthService.setSessionExpiredHandler(null);
    }, [navigate]);

    useEffect(() => {
        userAuthService.setCurrentUser(appUser);
        userAuthService.refreshCurrentUser()
            .then(() => {
                getNotificationCount();
            }).catch((isLoggedIn) => {
                if (!isLoggedIn) {
                    dispatch(setDisplayMessage(`Your session has timed out.`));
                    dispatch(setDisplayMessageType("warning"))
                    navigate('/');
                }
            })
    }, []);

    useInterval(() => {
        getNotificationCount();
    }, 5000);

    const getNotificationCount = () => {
        (new NotificationsService()).getUnreadNotificationsCount()
            .then((response) => {
                setNotificationCount(response);
            }).catch(() => {});
    }

    const handleOpenProfile = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElProfile(event.currentTarget);
    };

    const handleCloseProfile= () => {
        setAnchorElProfile(null);        
    }

    const onLogoutClicked = () => {
        guardAction(() => {
            userAuthService.logout();
            navigate('/');
        });
    }

    const onAdminClicked = () => {
        if (props.currentUser?.role !== "admin")
            return;
        guardAction(() => navigate('/app/admin'));
    }

    const isInQueryWorkflow = () => {
        return location.pathname.startsWith('/app/query') || location.pathname.startsWith('/app/refinequery');
    }

    const guardAction = (action: () => void) => {
        const onQueryBuilderPage = location.pathname.startsWith('/app/query');
        const needsConfirmation = isInQueryWorkflow() && (!onQueryBuilderPage || hasQueryTitle);
        if (needsConfirmation) {
            setPendingAction(() => action);
        } else {
            action();
        }
    }

    const onNavClicked = (target: string) => {
        guardAction(() => navigate(target));
    }

    const onCancelNav = () => {
        setPendingAction(null);
    }

    const onConfirmNav = () => {
        let action = pendingAction;
        setPendingAction(null);
        if (action) {
            action();
        }
    }

    const renderProfileSettings = () => {
        let currentUser = appUser;
        return (
            <>
            <Menu id="menu-user-settings"
                open={Boolean(anchorElProfile)}
                onClose={handleCloseProfile}
                sx={{ mt: '45px' }}
                anchorEl={anchorElProfile}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}                            
            >
                <MenuItem>
                    <Typography>
                        {props.currentUser?.fullName} ({props.currentUser?.username})
                    </Typography>
                </MenuItem>
                <MenuItem>
                    <Divider style={{width:'100%'}} />
                </MenuItem>
                <MenuItem>
                    <Typography>
                        Credit balance: {formatNumberString(currentUser?.creditBalance.availableBalance)} (available: {formatNumberString(currentUser?.creditBalance.availableBalance)})
                    </Typography>
                </MenuItem>
                <MenuItem>
                    <Divider style={{width:'100%'}} />
                </MenuItem>
                <MenuItem>
                    <Button onClick={onLogoutClicked}>Logout</Button>
                </MenuItem>
            </Menu>
            </>
        );
    }

    const renderAdminIcon = () => {
        if (!userAuthService.isAdmin()) {
            return (<></>);
        }

        if (props.numAccountsNeedingApproval !== adminBadgeCount) {
            setAdminBadgeCount(props.numAccountsNeedingApproval);
        }

        let adminTitle = `Administration${adminBadgeCount > 0 ? ": " + adminBadgeCount + " user(s) need approval" : ""}`;

        return (
            <Box sx={{flexGrow: 0 }}>
                <Tooltip title={adminTitle}>
                    <IconButton onClick={() => onAdminClicked()}>
                        <Badge
                            badgeContent={adminBadgeCount}
                            color="primary"
                            max={99}
                        >
                            <AdminPanelSettingsIcon />
                        </Badge>
                    </IconButton>
                </Tooltip>      
            </Box>
        );
    }
    
    return (
        <AppBar position="static" color="default" className="xsifter-appbar">
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        <a href="/" onClick={(event) => { event.preventDefault(); onNavClicked('/'); }}><img src={XSifterLogo} alt="XSifter Logo" /></a>
                    </Typography>
                    {userAuthService.isLoggedIn() && (
                        <>
                    <Box sx={{flexGrow: 0 }}>
                        <Button variant="outlined" className="x-new-query-btn" onClick={() => onNavClicked('/app/query?new=true')}>
                            <img src={XSifterXOnly} alt="Create new query" />
                            <span className="x-text-dark-blue">New</span> <span className="x-text-light-blue">Query</span>
                        </Button>
                        <Tooltip title="Notifications">
                            <IconButton onClick={() => guardAction(() => navigate("/app/notifications"))}>
                                {notificationCount > 0 ?
                                    <Badge
                                        badgeContent={notificationCount}
                                        color="primary"
                                        max={99}
                                    >
                                        <NotificationsActiveIcon />
                                    </Badge>
                                    : <NotificationsNoneIcon />
                                }
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <RunningTasksDisplay />
                    <Box sx={{flexGrow: 0 }}>
                        <Tooltip title="Saved queries">
                            <IconButton onClick={() => guardAction(() => navigate("/app/saved"))}>
                                <ManageSearchIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {renderAdminIcon()}
                    <Box sx={{flexGrow: 0 }}>
                        <Tooltip title="Profile Settings">
                            <IconButton onClick={handleOpenProfile}>
                                <AccountCircleIcon />
                            </IconButton>
                        </Tooltip>
                        { renderProfileSettings() }
                    </Box>
                </>)}
                </Toolbar>
            </Container>
            <Dialog open={pendingAction !== null} onClose={onCancelNav}>
                <DialogTitle>Stop query editing?</DialogTitle>
                <DialogContent dividers>
                    Are you sure you want to stop? You will lose any work you've done on this query if you leave this page.
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancelNav}>Cancel</Button>
                    <Button onClick={onConfirmNav}>Yes, stop</Button>
                </DialogActions>
            </Dialog>
        </AppBar>
    );
}

export default XSifterAppHeader;
